import {matchesSearchTerm} from "../../utilis/searchMatch";

export type TeamGroup = 'AVAILABLE' | 'IN_MEETING' | 'IN_CALL' | 'DND' | 'OFFLINE';
export type TeamFilter = 'ALL' | TeamGroup;
export type TeamSort = 'name' | 'status';

// TelcoStatus values as plain strings: importing the enum from teamService
// pulls the whole RTK store into this pure module and its tests.
type TelcoStatusValue = 'ONLINE' | 'TALKING' | 'OFFLINE' | 'UNKNOWN' | 'DND' | string;

export interface TeamMember {
    userId: string | number;
    firstName: string;
    lastName: string;
    position: string;
    email: string;
    avatarUrl: string;
    userTimeZone: string;
    customUserStatus?: {name: string; icon: string; dueDate: string} | null;
    telcoUserStatus?: {status: TelcoStatusValue} | null;
    doNotDisturbUserStatus?: {enabled: boolean} | null;
    meetingUserStatus?: {status: string; dateFrom?: string | null; dateTo: string} | null;
}

// Display order of the status pills, the grouped sections and the "Status" sort.
export const TEAM_GROUPS: {key: TeamGroup; label: string}[] = [
    {key: 'AVAILABLE', label: 'Available'},
    {key: 'IN_MEETING', label: 'In a meeting'},
    {key: 'IN_CALL', label: 'In a call'},
    {key: 'DND', label: 'Do not disturb'},
    {key: 'OFFLINE', label: 'Offline'},
];

const GROUP_ORDER = TEAM_GROUPS.map(group => group.key);

export const isInMeeting = (member: TeamMember): boolean =>
    member.meetingUserStatus?.status === 'IN_MEETING';

/**
 * Same mapping as the mobile Team screen (MobileCh3 TeamScreen getTeamGroup +
 * userWithStatusToStatus), so a colleague lands in the same group on both
 * clients: a meeting wins, then the DND flag, then the telco status.
 */
export const getTeamGroup = (member: TeamMember): TeamGroup => {
    if (isInMeeting(member)) return 'IN_MEETING';
    if (member.doNotDisturbUserStatus?.enabled) return 'DND';
    switch (member.telcoUserStatus?.status) {
        case 'ONLINE':
            return 'AVAILABLE';
        case 'TALKING':
            return 'IN_CALL';
        case 'DND':
        case 'BUSY':
            return 'DND';
        default:
            return 'OFFLINE';
    }
};

export const fullName = (member: TeamMember): string =>
    `${member.firstName} ${member.lastName}`.trim();

export const matchesTeamSearch = (member: TeamMember, query: string): boolean => {
    const term = query.trim();
    if (!term) return true;
    return [member.firstName, member.lastName, member.email, member.position]
        .some(field => Boolean(field) && matchesSearchTerm(field, term));
};

export const filterTeam = (members: TeamMember[], filter: TeamFilter, query: string): TeamMember[] =>
    members.filter(member =>
        (filter === 'ALL' || getTeamGroup(member) === filter) && matchesTeamSearch(member, query));

const compareNames = (a: TeamMember, b: TeamMember): number =>
    fullName(a).localeCompare(fullName(b), undefined, {sensitivity: 'base'});

export const sortTeam = (members: TeamMember[], sort: TeamSort): TeamMember[] =>
    [...members].sort((a, b) => {
        if (sort === 'status') {
            const byStatus = GROUP_ORDER.indexOf(getTeamGroup(a)) - GROUP_ORDER.indexOf(getTeamGroup(b));
            if (byStatus !== 0) return byStatus;
        }
        return compareNames(a, b);
    });

/** Splits already filtered and sorted members into status sections, skipping empty ones. */
export const groupTeam = (members: TeamMember[]): {group: TeamGroup; members: TeamMember[]}[] =>
    GROUP_ORDER
        .map(group => ({group, members: members.filter(member => getTeamGroup(member) === group)}))
        .filter(section => section.members.length > 0);
